import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { format, formatDistanceToNow } from "date-fns";
import {
  Check,
  CheckCircle2,
  FileText,
  GraduationCap,
  Loader2,
  MoreVertical,
  Pencil,
  Search,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { Page } from "@/components/layout";
import { ContentCard } from "../ui/ContentCard";
import { ContentViewerModal } from "../ui/ContentViewerModal";
import { PrimaryButton } from "../ui/PrimaryButton";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { useSession } from "@/hooks/useSession";
import { UserSession } from "@/types/session";
import { chatEntryTitleMap } from "@/config/chat.config";
import { navigateToSessionResume } from "@/lib/chatNavigation";
import { plainUserText } from "@/lib/plainUserText";
import { chatHistoryService } from "@/services/chatHistoryService";
import type { ChatHistoryRecord } from "@/types/history";
import {
  deleteSavedDecision,
  listSavedDecisions,
  savedDecisionAsHtmlDataUrl,
  savedDecisionsChangedEvent,
  setDecisionInResources,
  type SavedDecision,
} from "@/services/savedDecisionsService";
import {
  deleteSavedScenarioTakeaway,
  listSavedScenarioTakeaways,
  openTakeawayPrintWindow,
  savedScenarioTakeawaysChangedEvent,
  type SavedScenarioTakeaway,
} from "@/services/teacherScenarioExportService";
import { renderMarkdownDocumentHtml } from "@/lib/markdownPrint";

const HISTORY_PAGE_CLOSED_LIMIT = 40;

type SessionSearchIndex = {
  searchBlob: string;
  lastActivity: Date;
  preview: string;
};

export const HistoryPage = () => {
  const location = useLocation();
  const { userData, isAuthenticated } = useAuth();
  const userId = userData?.user_id;
  const {
    loadClosedSessions,
    closedSessions,
    isLoadingClosedSessions,
    reopenSessionFromHistory,
    renameSession,
    deleteSession,
  } = useSession();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchIndex, setSearchIndex] = useState<Record<string, SessionSearchIndex>>({});
  const [isBuildingSearchIndex, setIsBuildingSearchIndex] = useState(false);
  const [savedDecisions, setSavedDecisions] = useState<SavedDecision[]>([]);
  const [savedTakeaways, setSavedTakeaways] = useState<SavedScenarioTakeaway[]>([]);

  useEffect(() => {
    if (!isAuthenticated || location.pathname !== "/history") return;
    void loadClosedSessions(HISTORY_PAGE_CLOSED_LIMIT);
  }, [isAuthenticated, location.pathname, loadClosedSessions]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const refresh = () => {
      if (document.visibilityState === "visible" && location.pathname === "/history") {
        void loadClosedSessions(HISTORY_PAGE_CLOSED_LIMIT);
      }
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [isAuthenticated, location.pathname, loadClosedSessions]);

  useEffect(() => {
    setSavedDecisions(listSavedDecisions(userId));
  }, [userId]);

  useEffect(() => {
    const refresh = () => setSavedDecisions(listSavedDecisions(userId));
    window.addEventListener(savedDecisionsChangedEvent, refresh);
    return () => window.removeEventListener(savedDecisionsChangedEvent, refresh);
  }, [userId]);

  useEffect(() => {
    setSavedTakeaways(listSavedScenarioTakeaways(userId));
  }, [userId]);

  useEffect(() => {
    const refresh = () => setSavedTakeaways(listSavedScenarioTakeaways(userId));
    window.addEventListener(savedScenarioTakeawaysChangedEvent, refresh);
    return () => window.removeEventListener(savedScenarioTakeawaysChangedEvent, refresh);
  }, [userId]);

  const visibleDecisions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return savedDecisions;
    return savedDecisions.filter((d) => {
      const blob = `${d.title} ${d.excerpt} ${d.markdown}`.toLowerCase();
      return blob.includes(q);
    });
  }, [savedDecisions, searchQuery]);

  const visibleTakeaways = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return savedTakeaways;
    return savedTakeaways.filter((t) => {
      const blob = `${t.title} ${t.excerpt} ${t.markdown} ${t.scenarioTitle}`.toLowerCase();
      return blob.includes(q);
    });
  }, [savedTakeaways, searchQuery]);

  const handleDecisionToggleResources = useCallback((id: string, next: boolean) => {
    if (!userId) return;
    const updated = setDecisionInResources(userId, id, next);
    if (!updated) {
      toast({ title: "Couldn't update the decision." });
      return;
    }
    toast({
      title: next ? "Added to My Resources." : "Removed from My Resources.",
    });
  }, [userId]);

  const handleDecisionDelete = useCallback((id: string, title: string) => {
    if (!userId) return;
    deleteSavedDecision(userId, id);
    toast({ title: `Deleted "${title}".` });
  }, [userId]);

  const handleTakeawayDelete = useCallback(
    (id: string, title: string) => {
      if (!userId) return;
      deleteSavedScenarioTakeaway(userId, id);
      toast({ title: "Deleted", description: `"${title}" removed from History.` });
    },
    [userId],
  );

  const sortedSessions = useMemo(
    () => (closedSessions ? sortSessionsByRecent(closedSessions, searchIndex) : []),
    [closedSessions, searchIndex]
  );

  const sessionIdsKey = useMemo(() => sortedSessions.map((s) => s.id).join(","), [sortedSessions]);

  useEffect(() => {
    if (sortedSessions.length === 0) {
      setSearchIndex({});
      return;
    }

    let cancelled = false;

    void (async () => {
      setIsBuildingSearchIndex(true);
      try {
        const entries = await Promise.all(
          sortedSessions.map(async (s) => {
            try {
              const rows = await chatHistoryService.getChatHistoryBySessionId(s.id);
              return [s.id, buildHistorySearchIndex(rows)] as const;
            } catch {
              return [s.id, emptyIndex(s)] as const;
            }
          })
        );
        if (!cancelled) {
          setSearchIndex(Object.fromEntries(entries));
        }
      } finally {
        if (!cancelled) {
          setIsBuildingSearchIndex(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionIdsKey, sortedSessions]);

  const filteredSessions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sortedSessions;

    return sortedSessions.filter((s) => {
      const title = (s.session_name?.trim() || chatEntryTitleMap[s.chat_mode]).toLowerCase();
      if (title.includes(q)) return true;

      if (isBuildingSearchIndex) {
        return true;
      }

      const idx = searchIndex[s.id];
      if (!idx) return false;
      return idx.searchBlob.includes(q);
    });
  }, [sortedSessions, searchQuery, searchIndex, isBuildingSearchIndex]);

  return (
    <Page.Container>
      <Page.Header className="space-y-2">
        <h1 className="font-literata text-3xl font-semibold text-white md:text-4xl">History</h1>
        <p className="max-w-2xl font-inter text-sm leading-relaxed text-white/80 md:text-base">
          Continue the conversation where you left off — open a chat below to resume with full context.
        </p>
      </Page.Header>

      <Page.Content className="flex flex-1 flex-col gap-5 overflow-y-auto pr-2 md:pr-4">
        {isLoadingClosedSessions ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-white" aria-label="Loading" />
          </div>
        ) : (
          <>
            <div className="mx-auto w-full max-w-3xl">
              <label htmlFor="history-search" className="sr-only">
                Search your chats
              </label>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400"
                  aria-hidden
                />
                <input
                  id="history-search"
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search your chats…"
                  autoComplete="off"
                  className="w-full rounded-2xl border border-white/25 bg-white/95 py-3 pl-11 pr-4 font-inter text-sm text-stone-900 shadow-sm placeholder:text-stone-400 focus:border-bodhi-orange focus:outline-none focus:ring-2 focus:ring-bodhi-orange/25 md:text-base"
                />
              </div>
              {isBuildingSearchIndex && sortedSessions.length > 0 && (
                <p className="mt-2 flex items-center gap-2 font-inter text-xs text-white/65">
                  <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden />
                  Indexing messages for search…
                </p>
              )}
            </div>

            {visibleTakeaways.length > 0 && (
              <section className="mx-auto w-full max-w-3xl space-y-2.5">
                <header className="flex items-baseline justify-between gap-3 px-1">
                  <h2 className="font-literata text-lg font-semibold text-white md:text-xl">
                    Scenario Practice Takeaways
                  </h2>
                  <span className="font-inter text-xs text-white/65">
                    {visibleTakeaways.length} document{visibleTakeaways.length === 1 ? "" : "s"}
                  </span>
                </header>
                <ul className="flex flex-col gap-2.5">
                  {visibleTakeaways.map((takeaway) => (
                    <li key={takeaway.id}>
                      <TakeawayCard takeaway={takeaway} onDelete={handleTakeawayDelete} />
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {visibleDecisions.length > 0 && (
              <section className="mx-auto w-full max-w-3xl space-y-2.5">
                <header className="flex items-baseline justify-between gap-3 px-1">
                  <h2 className="font-literata text-lg font-semibold text-white md:text-xl">
                    Strategic Decision Documents
                  </h2>
                  <span className="font-inter text-xs text-white/65">
                    {visibleDecisions.length} document{visibleDecisions.length === 1 ? "" : "s"}
                  </span>
                </header>
                <ul className="flex flex-col gap-2.5">
                  {visibleDecisions.map((decision) => (
                    <li key={decision.id}>
                      <DecisionCard
                        decision={decision}
                        onToggleResources={handleDecisionToggleResources}
                        onDelete={handleDecisionDelete}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {sortedSessions.length === 0 && visibleDecisions.length === 0 && visibleTakeaways.length === 0 && (
              <p className="text-center font-inter text-white/75">
                Past conversations and saved decisions appear here once you start using the tools.
              </p>
            )}

            {sortedSessions.length > 0 && filteredSessions.length === 0 && !isBuildingSearchIndex && (
              <p className="text-center font-inter text-white/85">No chats match your search.</p>
            )}

            {sortedSessions.length > 0 && (
              <section className="mx-auto w-full max-w-3xl space-y-2.5 pb-4">
                <header className="flex items-baseline justify-between gap-3 px-1">
                  <h2 className="font-literata text-lg font-semibold text-white md:text-xl">
                    Conversations
                  </h2>
                  <span className="font-inter text-xs text-white/65">
                    {filteredSessions.length} chat{filteredSessions.length === 1 ? "" : "s"}
                  </span>
                </header>
                <ul className="flex flex-col gap-2.5">
                  {filteredSessions.map((session) => (
                    <li key={session.id}>
                      <SessionCard
                        session={session}
                        meta={searchIndex[session.id]}
                        reopenSessionFromHistory={reopenSessionFromHistory}
                        onRename={renameSession}
                        onDelete={deleteSession}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </Page.Content>
    </Page.Container>
  );
};

const DecisionCard = ({
  decision,
  onToggleResources,
  onDelete,
}: {
  decision: SavedDecision;
  onToggleResources: (id: string, next: boolean) => void;
  onDelete: (id: string, title: string) => void;
}) => {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const inResources = decision.addedToResources === true;
  const url = useMemo(() => savedDecisionAsHtmlDataUrl(decision), [decision]);
  const createdLabel = format(decision.createdAt, "MMM d, yyyy · h:mm a");

  return (
    <>
      <ContentCard className="border border-white/10 bg-white p-3 shadow-sm sm:p-3.5 md:p-4">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center gap-1 rounded-full bg-bodhi-teal/10 px-2 py-0.5 font-inter text-[10px] font-semibold uppercase tracking-wide text-bodhi-teal">
                <FileText className="h-3 w-3" aria-hidden /> Strategic Decision Document
              </span>
              {inResources && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-inter text-[10px] font-medium text-emerald-700 ring-1 ring-emerald-200">
                  <CheckCircle2 className="h-3 w-3" aria-hidden /> In My Resources
                </span>
              )}
            </div>
            <h3
              className="mt-1 truncate font-literata text-base font-semibold text-stone-900 md:text-[1.05rem]"
              title={decision.title}
            >
              {decision.title}
            </h3>
            <p className="mt-0.5 line-clamp-2 font-inter text-xs text-stone-500 md:text-sm">
              {decision.excerpt}
            </p>
            <p className="mt-1 font-inter text-[11px] text-stone-400 md:text-xs">
              Saved {createdLabel}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            <ContentViewerModal url={url} title={decision.title} type="website">
              <PrimaryButton size="sm" nativeType="button" className="px-4">
                Open
              </PrimaryButton>
            </ContentViewerModal>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-9 w-9 text-stone-500 hover:bg-stone-100"
                  aria-label="More actions"
                >
                  <MoreVertical className="h-4 w-4" aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    onToggleResources(decision.id, !inResources);
                  }}
                >
                  {inResources ? (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" aria-hidden /> Remove from My Resources
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" aria-hidden /> Add to My Resources
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    setConfirmDeleteOpen(true);
                  }}
                  className="text-red-600 focus:bg-red-50 focus:text-red-700"
                >
                  <Trash2 className="mr-2 h-4 w-4" aria-hidden />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </ContentCard>

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this Strategic Decision Document?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes &ldquo;{decision.title}&rdquo; from History
              {inResources ? " and from My Resources" : ""}. You can&rsquo;t undo this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                onDelete(decision.id, decision.title);
                setConfirmDeleteOpen(false);
              }}
              className="bg-red-600 text-white hover:bg-red-600/90 focus:ring-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const TakeawayCard = ({
  takeaway,
  onDelete,
}: {
  takeaway: SavedScenarioTakeaway;
  onDelete: (id: string, title: string) => void;
}) => {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const url = useMemo(
    () =>
      `data:text/html;charset=utf-8,${encodeURIComponent(
        renderMarkdownDocumentHtml(takeaway.markdown, {
          title: takeaway.title,
          metaLabel: "Scenario Practice Takeaway",
          createdAt: takeaway.createdAt,
        }),
      )}`,
    [takeaway],
  );
  const createdLabel = format(takeaway.createdAt, "MMM d, yyyy · h:mm a");

  return (
    <>
      <ContentCard className="border border-white/10 bg-white p-3 shadow-sm sm:p-3.5 md:p-4">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center gap-1 rounded-full bg-bodhi-teal/10 px-2 py-0.5 font-inter text-[10px] font-semibold uppercase tracking-wide text-bodhi-teal">
                <GraduationCap className="h-3 w-3" aria-hidden /> Scenario Practice
              </span>
              <span className="rounded-full bg-stone-100 px-2 py-0.5 font-inter text-[10px] font-medium capitalize text-stone-600">
                {takeaway.mode} mode
              </span>
            </div>
            <h3
              className="mt-1 truncate font-literata text-base font-semibold text-stone-900 md:text-[1.05rem]"
              title={takeaway.title}
            >
              {takeaway.title}
            </h3>
            <p className="mt-0.5 line-clamp-2 font-inter text-xs text-stone-500 md:text-sm">
              {takeaway.excerpt}
            </p>
            <p className="mt-1 font-inter text-[11px] text-stone-400 md:text-xs">
              Saved {createdLabel}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            <ContentViewerModal url={url} title={takeaway.title} type="website">
              <PrimaryButton size="sm" nativeType="button" className="px-4">
                Open
              </PrimaryButton>
            </ContentViewerModal>

            <PrimaryButton
              size="sm"
              nativeType="button"
              variant="outline"
              className="px-3"
              onClick={() => openTakeawayPrintWindow(takeaway.markdown, takeaway.scenarioTitle)}
            >
              Print
            </PrimaryButton>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-9 w-9 text-stone-500 hover:bg-stone-100"
                  aria-label="More actions"
                >
                  <MoreVertical className="h-4 w-4" aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    setConfirmDeleteOpen(true);
                  }}
                  className="text-red-600 focus:bg-red-50 focus:text-red-700"
                >
                  <Trash2 className="mr-2 h-4 w-4" aria-hidden />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </ContentCard>

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this Scenario Practice takeaway?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes &ldquo;{takeaway.title}&rdquo; from History. You can&rsquo;t undo this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                onDelete(takeaway.id, takeaway.title);
                setConfirmDeleteOpen(false);
              }}
              className="bg-red-600 text-white hover:bg-red-600/90 focus:ring-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

function emptyIndex(s: UserSession): SessionSearchIndex {
  const d = new Date(s.closed_at ?? s.created_at);
  return { searchBlob: "", lastActivity: d, preview: "" };
}

function sortSessionsByRecent(
  sessions: Record<string, UserSession>,
  searchIndex: Record<string, SessionSearchIndex>
): UserSession[] {
  return Object.values(sessions).sort((a, b) => {
    const aTs =
      searchIndex[a.id]?.lastActivity?.getTime() ?? new Date(a.closed_at ?? a.created_at).getTime();
    const bTs =
      searchIndex[b.id]?.lastActivity?.getTime() ?? new Date(b.closed_at ?? b.created_at).getTime();
    return bTs - aTs;
  });
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function buildHistorySearchIndex(records: ChatHistoryRecord[]): SessionSearchIndex {
  if (!records.length) {
    return { searchBlob: "", lastActivity: new Date(0), preview: "" };
  }

  let lastActivity = new Date(records[0]!.message.timestamp);
  const parts: string[] = [];
  let lastPreview = "";

  for (const row of records) {
    const ts = new Date(row.message.timestamp);
    if (ts > lastActivity) lastActivity = ts;
    const plain =
      row.message.role === "user"
        ? plainUserText(row.message.content)
        : stripHtml(row.message.content);
    parts.push(plain);
    lastPreview = plain;
  }

  const clipped = lastPreview.length > 120 ? `${lastPreview.slice(0, 117)}…` : lastPreview;

  return {
    searchBlob: parts.join("\n").toLowerCase(),
    lastActivity,
    preview: clipped,
  };
}

const SessionCard = ({
  session,
  meta,
  reopenSessionFromHistory,
  onRename,
  onDelete,
}: {
  session: UserSession;
  meta?: SessionSearchIndex;
  reopenSessionFromHistory: ReturnType<typeof useSession>["reopenSessionFromHistory"];
  onRename: ReturnType<typeof useSession>["renameSession"];
  onDelete: ReturnType<typeof useSession>["deleteSession"];
}) => {
  const navigate = useNavigate();
  const [isOpening, setIsOpening] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [isSavingName, setIsSavingName] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const editInputRef = useRef<HTMLInputElement>(null);

  const defaultTitle = chatEntryTitleMap[session.chat_mode];
  const displayTitle = session.session_name?.trim() || defaultTitle;
  const closedAt = session.closed_at ?? session.created_at;
  const lastTs = meta?.lastActivity && meta.lastActivity.getTime() > 0 ? meta.lastActivity : new Date(closedAt);
  const lastLabel = formatDistanceToNow(lastTs, { addSuffix: true });
  const subtitle = meta?.preview?.trim()
    ? meta.preview
    : `${format(closedAt, "MMM d, yyyy · h:mm a")} · ${defaultTitle}`;

  const resume = async () => {
    if (isOpening) return;
    setIsOpening(true);
    try {
      const next = await reopenSessionFromHistory(session.id);
      if (!next) return;
      navigateToSessionResume(navigate, next, true);
    } finally {
      setIsOpening(false);
    }
  };

  const startEditing = () => {
    setDraftName(session.session_name?.trim() || "");
    setIsEditing(true);
    requestAnimationFrame(() => editInputRef.current?.focus());
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setDraftName("");
  };

  const submitRename = async () => {
    const trimmed = draftName.trim();
    const current = session.session_name?.trim() || "";
    if (trimmed === current) {
      setIsEditing(false);
      return;
    }
    setIsSavingName(true);
    const result = await onRename(session.id, trimmed.length > 0 ? trimmed : null);
    setIsSavingName(false);
    if (result) {
      toast({ title: trimmed ? "Conversation renamed" : "Reset to default name" });
      setIsEditing(false);
    }
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    const ok = await onDelete(session.id);
    setIsDeleting(false);
    if (ok) {
      toast({ title: "Conversation deleted" });
      setConfirmDeleteOpen(false);
    }
  };

  return (
    <>
      <ContentCard className="border border-white/10 bg-white p-3 shadow-sm sm:p-3.5 md:p-4">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            {isEditing ? (
              <div className="flex items-center gap-2">
                <input
                  ref={editInputRef}
                  type="text"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void submitRename();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      cancelEditing();
                    }
                  }}
                  maxLength={120}
                  placeholder={defaultTitle}
                  disabled={isSavingName}
                  className="min-w-0 flex-1 rounded-lg border border-stone-200 bg-white px-2.5 py-1.5 font-literata text-base text-stone-900 shadow-inner focus:border-bodhi-orange focus:outline-none focus:ring-2 focus:ring-bodhi-orange/25 md:text-[1.05rem]"
                />
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() => void submitRename()}
                  disabled={isSavingName}
                  className="h-8 w-8 shrink-0 border-stone-200 text-bodhi-teal hover:bg-stone-50"
                  aria-label="Save name"
                >
                  {isSavingName ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Check className="h-4 w-4" aria-hidden />}
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={cancelEditing}
                  disabled={isSavingName}
                  className="h-8 w-8 shrink-0 text-stone-500 hover:bg-stone-100"
                  aria-label="Cancel rename"
                >
                  <X className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-1.5">
                <h2
                  className="truncate font-literata text-base font-semibold text-stone-900 md:text-[1.05rem]"
                  title={displayTitle}
                >
                  {displayTitle}
                </h2>
                {!session.closed_at && (
                  <span className="inline-flex shrink-0 items-center rounded-full bg-bodhi-teal/10 px-2 py-0.5 font-inter text-[10px] font-semibold uppercase tracking-wide text-bodhi-teal">
                    In progress
                  </span>
                )}
              </div>
            )}
            <p className="mt-0.5 line-clamp-1 font-inter text-xs text-stone-500 md:text-sm">{subtitle}</p>
            <p className="mt-1 font-inter text-[11px] text-stone-400 md:text-xs">Last message {lastLabel}</p>
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            <PrimaryButton
              size="sm"
              nativeType="button"
              disabled={isOpening || isEditing || isDeleting}
              onClick={() => void resume()}
              className="px-4"
            >
              {isOpening ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : "Open"}
            </PrimaryButton>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-9 w-9 text-stone-500 hover:bg-stone-100"
                  aria-label="More actions"
                  disabled={isEditing || isDeleting}
                >
                  <MoreVertical className="h-4 w-4" aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onSelect={(e) => { e.preventDefault(); startEditing(); }}>
                  <Pencil className="mr-2 h-4 w-4" aria-hidden />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => { e.preventDefault(); setConfirmDeleteOpen(true); }}
                  className="text-red-600 focus:bg-red-50 focus:text-red-700"
                >
                  <Trash2 className="mr-2 h-4 w-4" aria-hidden />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </ContentCard>

      <AlertDialog open={confirmDeleteOpen} onOpenChange={(open) => !isDeleting && setConfirmDeleteOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes &ldquo;{displayTitle}&rdquo; and its messages. You can&rsquo;t undo this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); void confirmDelete(); }}
              disabled={isDeleting}
              className="bg-red-600 text-white hover:bg-red-600/90 focus:ring-red-600"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> Deleting…
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
